<script>
import {ref} from "vue";
import useTools from "@/use/Tools";
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import axios from "axios";
import ImportModel from "@/views/Tools/Components/ImportModel.vue";
import {useSecurityStore} from "@/stores/Security";

export default {
    components: {ImportModel},
    setup() {
        const {mxRules} = useValidation();
        const {$swal, $i18n} = useTools();
        const securityStore = useSecurityStore();
        const {
            dialog,
            loading,
        } = useSharedData();
        dialog.value = false;

        const file = ref(null);
        const contratTemplates = ref([]);
        const search = ref(null);
        const logDialog = ref(false);
        const logs = ref([]);

        const headers = [
            {
                text: $i18n.t("common.code"),
                align: "start",
                sortable: true,
                value: "code",
            },
            {
                text: $i18n.t("common.label"),
                align: "start",
                sortable: true,
                value: "name",
            },
            {
                text: $i18n.t("contrat.duration"),
                align: "start",
                sortable: true,
                value: "duration",
            },
            {
                text: $i18n.t("contrat.trial_period"),
                align: "start",
                sortable: true,
                value: "trialPeriodDuration",
            },
            {
                text: $i18n.t("common.state"),
                align: "start",
                sortable: true,
                value: "archive",
            },
        ];


        function upload() {
            loading.value = true;
            let formData = new FormData();
            formData.append('file', file.value);
            axios.post(`/tools/getContratTemplates`, formData).then(response => {
                contratTemplates.value = response.data.elements;
                logs.value = response.data.logs;
                dialog.value = false;
                file.value = null;
                if (logs.value.length > 0)
                    logDialog.value = true;
            }).finally(() => {
                loading.value = false;
            });
        }

        function apply() {
            loading.value = true;
            axios.post(`/contratTemplates/saveAll`, contratTemplates.value).then(() => {
                $swal.fire('Très Bien', "Importation des modèles terminer", 'success')
                contratTemplates.value = [];
                logs.value = [];
            }).finally(() => {
                loading.value = false;
            });
        }

        return {
            securityStore,
            mxRules,
            dialog,
            loading,
            headers,
            file,
            search,
            contratTemplates,
            logDialog,
            logs,
            apply,
            upload,
        }
    }
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.contrat_template_tools')"
            show-total
            :total="contratTemplates.length"
        >
            <template v-slot:leftToolbar>
                <ImportModel file-name="Modele Contrat.xlsx"/>
            </template>
            <template v-slot:rightToolbar>
                <w-btn-import
                    @press="dialog = true"
                />
                <w-btn
                    color="orange"
                    icon="mdi-skull-scan"
                    :text="$t('common.logs')"
                    @press="logDialog = true"
                />
                <w-btn
                    v-if="securityStore.authorities['UPDATE_IMPORT_PARAM']"
                    color="red"
                    icon="mdi-table-check"
                    :text="$t('common.apply')"
                    :loading="loading"
                    @press="apply"
                />

            </template>

            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="4">
                            <w-text-field v-model="search" :label="$t('common.search')"/>
                        </v-col>
                    </v-row>
                </v-container>
                <w-data-table
                    :headers="headers"
                    identity="code"
                    :search="search"
                    :items="contratTemplates"
                    :loading="loading"
                >
                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip color="teal" :text="item.code"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-chip color="blue darken-2" :text="item.name"/>
                    </template>
                    <template v-slot:[`item.archive`]="{ item }">
                        <w-chip outlined :text="item.archive ? $t('common.archive') : $t('common.in_progress')"/>
                    </template>
                    <template v-slot:[`item.trialPeriodDuration`]="{ item }">
                        <w-chip
                            outlined
                            :text="item.trialPeriod ? `${item.trialPeriodDuration} ${$t(`common.${item.trialPeriodPeriodicity}`)}` : $t('contrat.without_trial_period')"
                        />
                        <strong
                            class="orange--text"
                            v-if="item.renewedTrialPeriod && item.trialPeriod"
                        >
                            , {{ $t('contrat.renewed_trial_period') }}
                        </strong>
                    </template>
                    <template v-slot:[`item.duration`]="{ item }">
                        <w-chip
                            outlined
                            :text="item.determinedTime ? `${item.duration} ${$t(`common.${item.periodicity}`)}` : $t('contrat.cdi')"
                        />
                    </template>
                </w-data-table>
            </template>
        </w-app>
        <v-dialog
            v-model="dialog"
            persistent
            max-width="700"
        >
            <v-card>
                <w-toolbar color="teal" :title="$t('common.import')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <strong>{{ $t('common.file') }} *</strong>
                                <w-file-input
                                    v-model="file"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="dialog = false"/>
                    <w-btn-import
                        :loading="loading"
                        @press="upload"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="logDialog"
            max-width="600"
            scrollable
        >
            <v-card>
                <w-toolbar color="orange" title="Logs"/>
                <v-card-text>
                    <v-container fluid>
                        <v-list dense>
                            <v-list-item
                                v-for="(log, index) in logs"
                                :key="`log-${index}`"
                            >
                                <v-list-item-title>{{ log }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>